/**
 * <s-product-gallery> — PDP carousel with desktop thumbnails.
 *
 * The scroll-snap track is the source of truth: swiping (mobile) or thumbnail
 * commands (desktop) move it, and the active thumbnail follows the scroll position.
 *
 * Required markup:
 *   [data-product-gallery]          — horizontal scroll-snap track, one slide per view.
 *   button[command="--go"][data-index] — thumbnail; commandfor points at this element's id.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.track = this.querySelector('[data-product-gallery]');
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    if (!this.track) return;

    this.addEventListener('command', this.#handleCommand, opts);
    this.track.addEventListener('scroll', this.#handleScroll, { ...opts, passive: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #handleCommand = (event) => {
    if (event.command !== '--go') return;
    const index = Number(event.source?.dataset.index || 0);
    const slide = this.track.children[index];
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isRtl = getComputedStyle(this.track).direction === 'rtl';
    const left = isRtl ? -index * this.track.clientWidth : index * this.track.clientWidth;
    this.track.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
    this.#setActive(index);
  };

  #handleScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const width = this.track.clientWidth || 1;
      const index = Math.round(Math.abs(this.track.scrollLeft) / width);
      this.#setActive(index);
    });
  };

  #setActive(index) {
    this.thumbs.forEach((thumb, i) => {
      if (i === index) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
